import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import DOMPurify from 'dompurify';

const BASE_URL = window.location.hostname === 'localhost' ? 'https://localhost:7000/api' : 'https://dealden.ca/api';

const TermsOfUsePage: React.FC = () => {
  const { data: contentPage, isLoading: isLoadingContent } = useQuery({
    queryKey: ['content-page', 'TermsOfUse'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${BASE_URL}/content-pages/TermsOfUse`);
        return response.data;
      } catch (error) {
        return null;
      }
    },
  });

  if (isLoadingContent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If dynamic content is available, show it with hero section
  if (contentPage) {
    return (
      <div className="min-h-screen bg-gray-50" dir="ltr">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li>
                  <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </li>
                <li>
                  <span className="text-gray-900 font-medium">{contentPage.title || 'Terms of Use'}</span>
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary-50 via-white to-blue-50 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-primary-200 to-blue-200 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center bg-gradient-to-r from-primary-500 to-blue-500 px-6 py-3 rounded-full mb-8 shadow-lg">
              <DocumentTextIcon className="h-5 w-5 text-white mr-2" />
              <span className="text-white font-semibold text-sm">Legal Terms</span>
            </div>
            
            <h1 className="text-6xl font-semibold text-gray-900 mb-8 leading-tight">
              Terms of 
              <span className="bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent"> Use</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Please read these Terms of Use carefully before using Dealden.ca. By accessing or using our platform, you agree to be bound by these terms.
            </p>
          </div>
        </section>

        {/* Dynamic Content */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-sm p-8 lg:p-12">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentPage.content) }}
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Fallback to static content
  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </li>
              <li>
                <span className="text-gray-900 font-medium">Terms of Use</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-primary-200 to-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-gradient-to-r from-primary-500 to-blue-500 px-6 py-3 rounded-full mb-8 shadow-lg">
            <DocumentTextIcon className="h-5 w-5 text-white mr-2" />
            <span className="text-white font-semibold text-sm">Legal Terms</span>
          </div>
          
          <h1 className="text-6xl font-semibold text-gray-900 mb-8 leading-tight">
            Terms of 
            <span className="bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent"> Use</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Please read these Terms of Use carefully before using Dealden.ca. By accessing or using our platform, you agree to be bound by these terms.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8 lg:p-12">
            <p className="text-gray-600">Content not available at this time.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfUsePage;

